import { db, newId, type StoredImage } from './schema';

/** A personal library of full-size phone photos hits hundreds of megabytes fast. */
const MAX_EDGE = 2000;

/** Boards render this. Longest edge, never upscaled. */
const THUMB_EDGE = 480;

const THUMB_MIME = 'image/webp';
const THUMB_QUALITY = 0.82;

export class ImageDecodeError extends Error {
  constructor(cause: unknown) {
    super("That file couldn't be read as an image.");
    this.name = 'ImageDecodeError';
    this.cause = cause;
  }
}

/**
 * Measure, downscale, thumbnail, store. Returns the new image id.
 *
 * The stage doc says downscale anything wider than 2000px; this uses the
 * longest edge instead, because a 4000px-tall portrait shot is exactly as
 * heavy as a 4000px-wide landscape one.
 */
export async function storeImage(file: Blob): Promise<string> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (error) {
    throw new ImageDecodeError(error);
  }

  try {
    const scale = fitScale(bitmap.width, bitmap.height, MAX_EDGE);

    // Untouched when it already fits: re-encoding a photo that needs no
    // resizing only loses quality.
    const full =
      scale < 1
        ? await draw(bitmap, Math.round(bitmap.width * scale), Math.round(bitmap.height * scale))
        : { blob: file, w: bitmap.width, h: bitmap.height, mime: file.type || 'image/png' };

    const thumbScale = fitScale(full.w, full.h, THUMB_EDGE);
    const thumb = await draw(
      bitmap,
      Math.max(1, Math.round(full.w * thumbScale)),
      Math.max(1, Math.round(full.h * thumbScale)),
    );

    const record: StoredImage = {
      id: newId(),
      blob: full.blob,
      thumb: thumb.blob,
      w: full.w,
      h: full.h,
      mime: full.mime,
      addedAt: Date.now(),
    };

    await db.images.add(record);
    return record.id;
  } finally {
    bitmap.close();
  }
}

export type Size = { w: number; h: number };

/**
 * Just the measurements. The masonry lays out the whole board before a single
 * photo has decoded, so it asks for hundreds of these at once and needs none
 * of the pixels.
 *
 * Cached because image records are immutable — nothing ever rewrites `w`/`h`,
 * only deletes the row — so a hit can't be stale. `forgetSizes` keeps deleted
 * ids from accumulating across a long session.
 */
const sizes = new Map<string, Size>();

export async function imageSizes(ids: readonly string[]): Promise<Map<string, Size>> {
  const missing = [...new Set(ids)].filter((id) => !sizes.has(id));

  if (missing.length > 0) {
    // Blobs come back as handles, not bytes — IndexedDB doesn't read the data
    // until something asks for it, and nothing here does.
    const rows = await db.images.bulkGet(missing);
    for (const row of rows) {
      if (row !== undefined) sizes.set(row.id, { w: row.w, h: row.h });
    }
  }

  const found = new Map<string, Size>();
  for (const id of ids) {
    const size = sizes.get(id);
    if (size !== undefined) found.set(id, size);
  }
  return found;
}

export function forgetSizes(ids: readonly string[]): void {
  for (const id of ids) sizes.delete(id);
}

/** Not a transaction on its own — callers fold it into theirs. */
export async function deleteImages(ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;
  await db.images.bulkDelete([...ids]);
  forgetSizes(ids);
}

function fitScale(w: number, h: number, maxEdge: number): number {
  const longest = Math.max(w, h);
  return longest > maxEdge ? maxEdge / longest : 1;
}

async function draw(
  bitmap: ImageBitmap,
  w: number,
  h: number,
): Promise<{ blob: Blob; w: number; h: number; mime: string }> {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new ImageDecodeError('2d canvas context unavailable');

  ctx.drawImage(bitmap, 0, 0, w, h);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, THUMB_MIME, THUMB_QUALITY);
  });
  if (blob === null) throw new ImageDecodeError('canvas.toBlob returned nothing');

  return { blob, w, h, mime: blob.type };
}
